import { useEffect, useRef, useState, useMemo } from "react";
import { api, formatApiError } from "@/api/client";
import { FileText, Image, FileArchive, Upload, Trash2, Download, Search, Folder, FolderLock, X, Pencil, Lock, LockOpen } from "lucide-react";
import { BulkBar, RowCheckbox, bulkDelete } from "@/components/BulkBar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const iconFor = (ct = "") => {
  if (ct.startsWith("image/")) return Image;
  if (ct.includes("pdf")) return FileText;
  if (ct.includes("zip") || ct.includes("compressed")) return FileArchive;
  return FileText;
};

const fmtSize = (b = 0) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const fmtDT = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [folders, setFolders] = useState([]); // [{name, protected, count}]
  const [folder, setFolder] = useState("all");
  const [folderTokens, setFolderTokens] = useState({}); // {name: folder_token}
  const [lockedDocs, setLockedDocs] = useState({}); // {name: [docs]}
  const [unlockPrompt, setUnlockPrompt] = useState(null); // folder name awaiting password
  const [lockSetting, setLockSetting] = useState(null); // {name, protected, isNew}
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pwSetting, setPwSetting] = useState(null);
  const [pwPrompt, setPwPrompt] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setBusy(true); setErr("");
    try {
      const [d, f] = await Promise.all([api.get("/documents"), api.get("/documents/folders/list")]);
      setDocs(d.data);
      setFolders(f.data.length ? f.data : [{ name: "General", protected: false, count: 0 }]);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const folderMeta = folders.find((x) => x.name === folder);

  const loadLockedFolder = async (name, token) => {
    try {
      const r = await api.get("/documents", { params: { folder: name, folder_token: token } });
      setLockedDocs((m) => ({ ...m, [name]: r.data }));
    } catch (e) { setErr(formatApiError(e)); }
  };

  const selectFolder = (f) => {
    if (f.protected && !folderTokens[f.name]) { setUnlockPrompt(f.name); return; }
    setFolder(f.name);
  };

  const onUnlocked = async (name, token) => {
    setFolderTokens((m) => ({ ...m, [name]: token }));
    setUnlockPrompt(null);
    setFolder(name);
    await loadLockedFolder(name, token);
  };

  const filtered = useMemo(() => {
    const meta = folders.find((x) => x.name === folder);
    let list;
    if (meta?.protected) list = lockedDocs[folder] || [];
    else if (folder !== "all") list = docs.filter((d) => (d.folder || "General") === folder);
    else list = docs;
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((d) => d.filename.toLowerCase().includes(s) || (d.note || "").toLowerCase().includes(s));
    }
    return list;
  }, [docs, lockedDocs, folders, folder, q]);

  const uploadFiles = async (files, tgtFolder) => {
    if (!files || !files.length) return;
    setUploading(true); setErr(""); setProgress(0);
    let done = 0;
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("kind", "document");
        fd.append("folder", tgtFolder || (folder !== "all" ? folder : "General"));
        await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        done += 1;
        setProgress(Math.round((done / files.length) * 100));
      }
      await load();
      if (folderMeta?.protected && folderTokens[folder]) await loadLockedFolder(folder, folderTokens[folder]);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setUploading(false); setProgress(0); }
  };

  const onPickFiles = (e) => uploadFiles(Array.from(e.target.files || []));

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragOver(false);
    uploadFiles(Array.from(e.dataTransfer.files || []));
  };

  const download = (d) => {
    if (d.password_protected) {
      setPwPrompt(d);
      return;
    }
    window.open(`${BACKEND_URL}/api/files/${d.id}`, "_blank");
  };

  const reloadAll = async () => {
    await load();
    if (folderMeta?.protected && folderTokens[folder]) await loadLockedFolder(folder, folderTokens[folder]);
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.filename}"?`)) return;
    try { await api.delete(`/documents/${d.id}`); await reloadAll(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const filteredIds = filtered.map((d) => d.id);
  const allSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
  const someSelected = filteredIds.some((id) => selected.has(id));
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allSelected) filteredIds.forEach((id) => n.delete(id));
    else filteredIds.forEach((id) => n.add(id));
    return n;
  });
  const clearSel = () => setSelected(new Set());
  const doBulkDelete = async () => {
    const ids = Array.from(selected);
    if (!window.confirm(`Delete ${ids.length} document(s)?`)) return;
    setBulkBusy(true);
    const ok = await bulkDelete(ids, (id) => api.delete(`/documents/${id}`));
    setBulkBusy(false); clearSel(); await reloadAll();
    if (ok < ids.length) setErr(`${ids.length - ok} deletion(s) failed.`);
  };

  const folderCounts = useMemo(() => {
    const c = { all: docs.length };
    folders.forEach((f) => { c[f.name] = f.count; });
    return c;
  }, [docs, folders]);

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-eyebrow text-primary">Cloud drive</p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight">Doc Drive</h1>
          <p className="mt-1 text-sm text-muted-foreground">Upload, organize and share PDFs, images, spreadsheets and any file up to 25 MB.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" multiple onChange={onPickFiles} className="hidden" data-testid="docs-file-input" />
          <button
            data-testid="docs-new-protected-folder"
            onClick={() => setLockSetting({ name: "", protected: false, isNew: true })}
            className="inline-flex items-center gap-1.5 rounded-md border border-border/70 px-3 py-2 text-xs font-semibold text-muted-foreground hover:border-amber-500/60 hover:text-amber-500"
          >
            <FolderLock className="h-3.5 w-3.5" /> Protected folder
          </button>
          <button
            data-testid="docs-upload-button"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            <Upload className="h-3.5 w-3.5" />
            {uploading ? `Uploading… ${progress}%` : "Upload files"}
          </button>
        </div>
      </header>

      {err && <div data-testid="docs-error" className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        data-testid="docs-drop-zone"
        className={`mb-6 rounded-md border-2 border-dashed p-8 text-center transition-colors ${dragOver ? "border-primary bg-primary/5" : "border-border/60"}`}
      >
        <Upload className={`mx-auto mb-2 h-8 w-8 ${dragOver ? "text-primary" : "text-muted-foreground"}`} />
        <p className="font-heading text-base font-semibold">{dragOver ? "Drop to upload" : "Drag & drop files here"}</p>
        <p className="mt-1 text-xs text-muted-foreground">
          or <button onClick={() => fileRef.current?.click()} className="text-primary underline underline-offset-2">browse from your device</button> · max 25 MB per file
        </p>
        {uploading && (
          <div className="mx-auto mt-4 h-2 max-w-md overflow-hidden rounded-full bg-secondary">
            <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>

      {/* Folder chips + search */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <button
          data-testid="docs-folder-all"
          onClick={() => setFolder("all")}
          className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium ${folder === "all" ? "border-primary bg-primary/10 text-primary" : "border-border/70 text-muted-foreground hover:bg-secondary"}`}
        >
          All <span className="rounded-sm bg-secondary px-1.5 py-0.5 text-[10px] text-foreground/70">{folderCounts.all || 0}</span>
        </button>
        {folders.map((f) => (
          <button
            key={f.name}
            data-testid={`docs-folder-${f.name}`}
            onClick={() => selectFolder(f)}
            className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium ${folder === f.name ? "border-primary bg-primary/10 text-primary" : "border-border/70 text-muted-foreground hover:bg-secondary"}`}
          >
            {f.protected ? <Lock className="h-3 w-3 text-amber-500" /> : <Folder className="h-3 w-3" />} {f.name}
            <span className="rounded-sm bg-secondary px-1.5 py-0.5 text-[10px] text-foreground/70">{folderCounts[f.name] || 0}</span>
          </button>
        ))}
        {folder !== "all" && (
          <button
            data-testid="docs-folder-lock-btn"
            onClick={() => setLockSetting({ name: folder, protected: !!folderMeta?.protected, isNew: false })}
            title={folderMeta?.protected ? "Change/remove folder password" : "Password-protect this folder"}
            className={`inline-flex items-center gap-1 rounded-md border border-border/70 px-2.5 py-1.5 text-xs font-medium hover:border-amber-500/60 hover:text-amber-500 ${folderMeta?.protected ? "text-amber-500" : "text-muted-foreground"}`}
          >
            {folderMeta?.protected ? <Lock className="h-3 w-3" /> : <LockOpen className="h-3 w-3" />}
            {folderMeta?.protected ? "Locked" : "Lock folder"}
          </button>
        )}
        <div className="relative ml-auto max-w-xs flex-1">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <input
            data-testid="docs-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search filename or note…"
            className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm"
          />
        </div>
      </div>

      <div className="mb-3">
        {filtered.length > 0 && (
          <label className="inline-flex items-center gap-2 rounded-md border border-border/70 px-3 py-2 text-xs">
            <RowCheckbox testid="docs-select-all" checked={allSelected} indeterminate={!allSelected && someSelected} onChange={toggleAll} />
            Select all
          </label>
        )}
      </div>

      {/* Grid */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((d) => {
          const Icon = iconFor(d.content_type);
          return (
            <div key={d.id} data-testid={`doc-card-${d.id}`} className={`sharp-card relative p-4 transition-all hover:-translate-y-0.5 hover:border-primary/40 ${selected.has(d.id) ? "ring-2 ring-primary/60" : ""}`}>
              <div className="absolute right-3 top-3">
                <RowCheckbox testid={`doc-select-${d.id}`} checked={selected.has(d.id)} onChange={() => toggleOne(d.id)} />
              </div>
              <div className="flex items-start gap-3 pr-6">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-heading text-sm font-semibold" title={d.filename}>{d.filename}</p>
                  <p className="mt-0.5 text-[11px] uppercase tracking-wider text-muted-foreground">
                    {d.folder || "General"} · {fmtSize(d.size)}
                  </p>
                  {d.note && <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{d.note}</p>}
                </div>
              </div>
              <p className="mt-3 text-[11px] text-muted-foreground">
                {d.uploaded_by_name || "Someone"} · {fmtDT(d.created_at)}
              </p>
              {d.password_protected && (
                <p className="mt-1 inline-flex items-center gap-1 rounded-md bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-500">
                  <Lock className="h-3 w-3" /> Password required
                </p>
              )}
              <div className="mt-3 flex justify-end gap-1.5">
                <button data-testid={`doc-download-${d.id}`} onClick={() => download(d)} className="inline-flex items-center gap-1 rounded-md border border-border/70 px-2 py-1 text-xs text-muted-foreground hover:border-primary/60 hover:text-primary">
                  <Download className="h-3.5 w-3.5" /> Download
                </button>
                <button
                  data-testid={`doc-password-${d.id}`}
                  onClick={() => setPwSetting(d)}
                  title={d.password_protected ? "Change/remove download password" : "Set download password"}
                  className={`inline-flex items-center gap-1 rounded-md border border-border/70 px-2 py-1 text-xs hover:border-primary/60 hover:text-primary ${d.password_protected ? "text-amber-500" : "text-muted-foreground"}`}
                >
                  {d.password_protected ? <Lock className="h-3.5 w-3.5" /> : <LockOpen className="h-3.5 w-3.5" />}
                </button>
                <button data-testid={`doc-edit-${d.id}`} onClick={() => setEditing(d)} className="inline-flex items-center gap-1 rounded-md border border-border/70 px-2 py-1 text-xs text-muted-foreground hover:border-primary/60 hover:text-primary">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
                <button data-testid={`doc-delete-${d.id}`} onClick={() => remove(d)} className="inline-flex items-center gap-1 rounded-md border border-border/70 px-2 py-1 text-xs text-muted-foreground hover:border-destructive/60 hover:text-destructive">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          );
        })}
        {!busy && filtered.length === 0 && (
          <p className="col-span-full py-12 text-center text-sm text-muted-foreground">No documents in this folder yet.</p>
        )}
      </div>

      <BulkBar count={selected.size} entityLabel="documents" onClear={clearSel} onDelete={doBulkDelete} busy={bulkBusy} />

      {editing && <EditDocumentModal doc={editing} folders={folders.map((f) => f.name)} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reloadAll(); }} />}
      {pwSetting && <SetPasswordModal doc={pwSetting} onClose={() => setPwSetting(null)} onSaved={() => { setPwSetting(null); reloadAll(); }} />}
      {pwPrompt && <PasswordPromptModal doc={pwPrompt} onClose={() => setPwPrompt(null)} />}
      {unlockPrompt && <FolderUnlockModal name={unlockPrompt} onClose={() => setUnlockPrompt(null)} onUnlocked={onUnlocked} />}
      {lockSetting && (
        <FolderLockModal
          folder={lockSetting}
          onClose={() => setLockSetting(null)}
          onSaved={(name, nowProtected) => {
            setLockSetting(null);
            setFolderTokens((m) => { const n = { ...m }; delete n[name]; return n; });
            if (!nowProtected && folder === name) setFolder("all");
            if (nowProtected && folder === name) setFolder("all");
            load();
          }}
        />
      )}
    </div>
  );
}

function SetPasswordModal({ doc, onClose, onSaved }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setBusy(true); setErr(""); setOk(false);
    try {
      await api.post(`/documents/${doc.id}/password`, { password: pw || null });
      setOk(true);
      setTimeout(onSaved, 800);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    setBusy(true); setErr("");
    try { await api.post(`/documents/${doc.id}/password`, { password: null }); onSaved(); }
    catch (e) { setErr(formatApiError(e)); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-md sharp-card" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <h3 className="font-heading text-lg font-semibold inline-flex items-center gap-2">
            <Lock className="h-4 w-4 text-amber-500" /> Download password
          </h3>
          <button data-testid="doc-password-close" onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={save} className="grid gap-3 p-5">
          <p className="text-xs text-muted-foreground">
            Setting a password for <span className="font-medium text-foreground">{doc.filename}</span>.
            Anyone downloading it will need to enter this password first.
          </p>
          <div>
            <label className="label-eyebrow mb-1.5 block">New password (min 4 characters)</label>
            <input data-testid="doc-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} minLength={4} required className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          {err && <div data-testid="doc-password-error" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          {ok && <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-500">Password saved.</div>}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
            {doc.password_protected && (
              <button type="button" data-testid="doc-password-clear" onClick={clear} disabled={busy}
                className="inline-flex items-center gap-1 rounded-md border border-border/70 px-3 py-2 text-xs text-muted-foreground hover:border-destructive/60 hover:text-destructive disabled:opacity-60">
                <LockOpen className="h-3.5 w-3.5" /> Remove password
              </button>
            )}
            <div className="ml-auto flex gap-2">
              <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
              <button data-testid="doc-password-save" disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
                {busy ? "Saving…" : "Save password"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function PasswordPromptModal({ doc, onClose }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const r = await api.post(`/documents/${doc.id}/verify-password`, { password: pw });
      const dlToken = r.data?.dl_token;
      if (!dlToken) { setErr("Server did not return a download token."); setBusy(false); return; }
      window.open(`${BACKEND_URL}/api/files/${doc.id}?dl_token=${encodeURIComponent(dlToken)}`, "_blank");
      onClose();
    } catch (e) { setErr(formatApiError(e)); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-md sharp-card" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <h3 className="font-heading text-lg font-semibold inline-flex items-center gap-2">
            <Lock className="h-4 w-4 text-amber-500" /> Password required
          </h3>
          <button data-testid="doc-prompt-close" onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="grid gap-3 p-5">
          <p className="text-xs text-muted-foreground">
            <span className="font-medium text-foreground">{doc.filename}</span> is password-protected. Enter the download password to continue.
          </p>
          <div>
            <label className="label-eyebrow mb-1.5 block">Password</label>
            <input data-testid="doc-prompt-input" autoFocus type="password" required value={pw} onChange={(e) => setPw(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          {err && <div data-testid="doc-prompt-error" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
            <button data-testid="doc-prompt-submit" disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {busy ? "Verifying…" : "Unlock & download"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FolderUnlockModal({ name, onClose, onUnlocked }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const r = await api.post("/documents/folders/verify", { folder: name, password: pw });
      await onUnlocked(name, r.data?.folder_token);
    } catch (e) { setErr(formatApiError(e)); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-md sharp-card" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <h3 className="font-heading text-lg font-semibold inline-flex items-center gap-2">
            <Lock className="h-4 w-4 text-amber-500" /> Locked folder
          </h3>
          <button data-testid="folder-unlock-close" onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="grid gap-3 p-5">
          <p className="text-xs text-muted-foreground">
            The folder <span className="font-medium text-foreground">{name}</span> is password-protected. Enter the folder password to open it.
          </p>
          <div>
            <label className="label-eyebrow mb-1.5 block">Folder password</label>
            <input data-testid="folder-unlock-input" autoFocus type="password" required value={pw} onChange={(e) => setPw(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          {err && <div data-testid="folder-unlock-error" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
            <button data-testid="folder-unlock-submit" disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {busy ? "Unlocking…" : "Unlock folder"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FolderLockModal({ folder, onClose, onSaved }) {
  const [name, setName] = useState(folder.name || "");
  const [pw, setPw] = useState("");
  const [currentPw, setCurrentPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await api.post("/documents/folders/password", {
        folder: name.trim(),
        password: pw || null,
        current_password: currentPw || null,
      });
      onSaved(name.trim(), true);
    } catch (e) { setErr(formatApiError(e)); setBusy(false); }
  };
  const removeLock = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/documents/folders/password", {
        folder: name.trim(),
        password: null,
        current_password: currentPw || null,
      });
      onSaved(name.trim(), false);
    } catch (e) { setErr(formatApiError(e)); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-md sharp-card" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <h3 className="font-heading text-lg font-semibold inline-flex items-center gap-2">
            <FolderLock className="h-4 w-4 text-amber-500" />
            {folder.isNew ? "New protected folder" : folder.protected ? "Change folder lock" : "Lock folder"}
          </h3>
          <button data-testid="folder-lock-close" onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={save} className="grid gap-3 p-5">
          <p className="text-xs text-muted-foreground">
            {folder.isNew
              ? "Create a password-protected folder. Anyone opening it must enter the password. Move or upload documents into it afterwards."
              : `Anyone opening the folder "${folder.name}" will need to enter this password.`}
          </p>
          {folder.isNew ? (
            <div>
              <label className="label-eyebrow mb-1.5 block">Folder name</label>
              <input data-testid="folder-lock-name" autoFocus required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Confidential" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
          ) : null}
          {folder.protected && (
            <div>
              <label className="label-eyebrow mb-1.5 block">Current password (super admins can skip)</label>
              <input data-testid="folder-lock-current" type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
          )}
          <div>
            <label className="label-eyebrow mb-1.5 block">{folder.protected ? "New password (min 4 characters)" : "Password (min 4 characters)"}</label>
            <input data-testid="folder-lock-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} minLength={4} required className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          {err && <div data-testid="folder-lock-error" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
            {folder.protected && (
              <button type="button" data-testid="folder-lock-remove" onClick={removeLock} disabled={busy}
                className="inline-flex items-center gap-1 rounded-md border border-border/70 px-3 py-2 text-xs text-muted-foreground hover:border-destructive/60 hover:text-destructive disabled:opacity-60">
                <LockOpen className="h-3.5 w-3.5" /> Remove lock
              </button>
            )}
            <div className="ml-auto flex gap-2">
              <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
              <button data-testid="folder-lock-save" disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
                {busy ? "Saving…" : "Save lock"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditDocumentModal({ doc, folders, onClose, onSaved }) {
  const [filename, setFilename] = useState(doc.filename);
  const [folder, setFolder] = useState(doc.folder || "General");
  const [customFolder, setCustomFolder] = useState("");
  const [note, setNote] = useState(doc.note || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = {
        filename,
        note,
        folder: customFolder.trim() || folder,
      };
      await api.patch(`/documents/${doc.id}`, payload);
      onSaved();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-lg sharp-card" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <h3 className="font-heading text-lg font-semibold">Edit document</h3>
          <button data-testid="doc-edit-close" onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="grid gap-3 p-5">
          <div>
            <label className="label-eyebrow mb-1.5 block">Filename</label>
            <input data-testid="doc-edit-filename" value={filename} onChange={(e) => setFilename(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Folder</label>
            <select data-testid="doc-edit-folder" value={folder} onChange={(e) => setFolder(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              {folders.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
            <input data-testid="doc-edit-folder-new" value={customFolder} onChange={(e) => setCustomFolder(e.target.value)} placeholder="or type a new folder name" className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Note (optional)</label>
            <textarea data-testid="doc-edit-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          {err && <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
            <button data-testid="doc-edit-submit" disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
